import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import Backdrop from '@mui/material/Backdrop';
import Fade from '@mui/material/Fade';
import Box from '@mui/material/Box';
import IconPicker from '../util/IconPicker';
import PortalTable, { PortalFieldDef } from '../util/PortalTable';

const COLOR_SWATCHES = ['#AFA9EC', '#5DCAA5', '#F0997B', '#85B7EB', '#ED93B1', '#97C459'];
const autoSlug = (v: string) => v.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

const MODAL_STYLE = {
  position: 'absolute' as const, top: '50%', left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 380, bgcolor: 'var(--bg-form)',
  border: 'var(--border-input)', borderRadius: '5px', boxShadow: 24, p: 3,
};

function TypeModal({ open, onClose, onSaved, editing, endpoint, titleLabel }: {
  open: boolean; onClose: () => void; onSaved: () => void;
  editing: any; endpoint: string; titleLabel: string;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [color, setColor] = useState('#6366f1');
  const [icon, setIcon] = useState('business');

  useEffect(() => {
    if (open) {
      setName(editing?.name ?? '');
      setSlug(editing?.slug ?? '');
      setColor(editing?.color ?? '#6366f1');
      setIcon(editing?.icon ?? 'business');
    }
  }, [open, editing]);

  const handleSave = async () => {
    if (!name.trim()) { enqueueSnackbar('Name is required', { variant: 'error' }); return; }
    try {
      if (editing) {
        await axios.put(`${endpoint}/${editing.id}`, { name, slug, color, icon });
      } else {
        await axios.post(endpoint, { name, slug: slug || autoSlug(name), color, icon });
      }
      enqueueSnackbar(editing ? 'Updated' : 'Created', { variant: 'success' });
      onSaved();
      onClose();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message ?? 'Error', { variant: 'error' });
    }
  };

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 500 } }}>
      <Fade in={open}>
        <Box sx={MODAL_STYLE}>
          <div className="text-[16px] font-semibold mb-[14px] text-[var(--text-color)]">
            {editing ? t('Edit') : t('Create')} {titleLabel}
          </div>
          <div className="flex flex-col gap-[10px]">
            <div>
              <label className="text-[12px] ml-[2px] text-[var(--text-color)]">{t('Name')}</label>
              <input title={t('Name')} placeholder={t('Name')}
                className="w-full text-[14px] py-[5px] px-[8px] rounded-[5px] bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input mt-[2px]"
                value={name}
                onChange={(e) => { setName(e.target.value); if (!editing) setSlug(autoSlug(e.target.value)); }}
              />
            </div>
            <div>
              <label className="text-[12px] ml-[2px] text-[var(--text-color)]">{t('Slug')}</label>
              <input title={t('Slug')} placeholder={t('Slug')}
                className="w-full text-[14px] py-[5px] px-[8px] rounded-[5px] bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input mt-[2px]"
                value={slug}
                onChange={(e) => setSlug(autoSlug(e.target.value))}
              />
            </div>
            <div className="flex items-center gap-[10px]">
              <label className="text-[12px] text-[var(--text-color)]">{t('ColorType')}</label>
              <div className="relative w-[36px] h-[36px] rounded-full overflow-hidden">
                <div className="w-full h-full rounded-full" style={{ backgroundColor: color }} />
                <input type="color" title={t('ColorType')} value={color} onChange={(e) => setColor(e.target.value)}
                  className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" />
              </div>
              <span className="text-[13px] text-[var(--text-color)] opacity-70">{color}</span>
            </div>
            <IconPicker value={icon} onChange={setIcon} color={color} />
          </div>
          <div className="flex gap-[10px] mt-[16px]">
            <Button variant="contained" sx={{ flex: 1, fontSize: 13 }} onClick={handleSave}>{t('Change')}</Button>
            <Button variant="contained" sx={{ flex: 1, fontSize: 13 }} onClick={onClose}>{t('Cancel')}</Button>
          </div>
        </Box>
      </Fade>
    </Modal>
  );
}

interface CrmEntityTypeTableProps {
  endpoint: string;
  title: string;
  addLabel: string;
}

const FIELDS: PortalFieldDef[] = [
  {
    key: 'name',
    label: 'Name',
    type: 'text',
    showInTable: true,
    editable: true,
    defaultValue: '',
  },
  {
    key: 'slug',
    label: 'Slug',
    type: 'slug',
    slugFrom: 'name',
    showInTable: false,
    editable: true,
    defaultValue: '',
  },
  {
    key: 'color',
    label: 'Color',
    type: 'color',
    swatches: COLOR_SWATCHES,
    showInTable: false,
    editable: true,
    defaultValue: '#AFA9EC',
  },
  {
    key: 'icon',
    label: 'Icon',
    type: 'icon',
    colorFrom: 'color',
    showInTable: false,
    editable: true,
    defaultValue: 'business',
  },
  {
    key: 'isActive',
    label: 'Visible',
    type: 'boolean',
    variant: 'toggle',
    showInTable: true,
    editable: true,
    defaultValue: true,
    align: 'right',
    width: 80,
  },
];

export default function CrmEntityTypeTable({ endpoint, title, addLabel }: CrmEntityTypeTableProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [types, setTypes] = useState<any[]>([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState<any>(null);

  const load = async () => {
    try {
      const { data } = await axios.get(endpoint);
      setTypes(Array.isArray(data) ? data : []);
    } catch { enqueueSnackbar('Error loading', { variant: 'error' }); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [endpoint]);

  const handleDelete = async (id: number, isSystem: boolean) => {
    if (isSystem) { enqueueSnackbar('System types cannot be deleted', { variant: 'warning' }); return; }
    if (!window.confirm(t('AreYouSure') + '?')) return;
    try {
      await axios.delete(`${endpoint}/${id}`);
      enqueueSnackbar('Deleted', { variant: 'success' });
      load();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message ?? 'Error', { variant: 'error' });
    }
  };

  const handleToggleField = async (row: any, fieldKey: string) => {
    try {
      await axios.put(`${endpoint}/${row.id}`, { [fieldKey]: !row[fieldKey] });
      load();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message ?? 'Error', { variant: 'error' });
    }
  };

  const handleDeleteMany = async (ids: (string | number)[]) => {
    await Promise.all(ids.map((id) => axios.delete(`${endpoint}/${id}`)));
    enqueueSnackbar('Deleted', { variant: 'success' });
    load();
  };

  const handleInlineAdd = async (data: Record<string, any>) => {
    if (!data.name?.trim()) {
      enqueueSnackbar('Name is required', { variant: 'error' });
      throw new Error('validation');
    }
    await axios.post(endpoint, {
      name: data.name.trim(),
      slug: data.slug || data.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''),
      color: data.color,
      icon: data.icon ?? 'business',
    });
    enqueueSnackbar('Created', { variant: 'success' });
    load();
  };

  return (
    <>
      <PortalTable
        title={title}
        rows={types}
        getKey={(ot) => ot.id}
        fields={FIELDS}
        onInlineAdd={handleInlineAdd}
        addLabel={addLabel}
        onEdit={(ot) => { setEditing(ot); setModal(true); }}
        onDelete={(ot) => handleDelete(ot.id, ot.isSystem)}
        onDeleteMany={handleDeleteMany}
        onToggleField={handleToggleField}
        canDelete={(ot) => !ot.isSystem}
        getRowOpacity={(ot) => ot.isActive ? 1 : 0.45}
      />
      <TypeModal
        open={modal}
        onClose={() => setModal(false)}
        onSaved={load}
        editing={editing}
        endpoint={endpoint}
        titleLabel={title}
      />
    </>
  );
}
